import { useEffect, useState } from 'react';
import { Button, FloatingLabel, Form } from 'react-bootstrap';
import { BrowserRouter, useHistory, useLocation } from 'react-router-dom';
import { isEmpty, mapValues, pick } from 'lodash';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import qs from 'qs';
import CardSkeleton from '../card-skeleton';
import ExperienceCard from '../experience-card';
import { useExperienceFilter } from './api';

const ExperienceFilterKernel = ({
  initialCategory,
  filters,
  displayAllEntries = true,
}) => {
  const location = useLocation();
  const history = useHistory();

  const getQueryObjectFromUrl = () => {
    const { search } = location;
    const searchParams = new URLSearchParams(search);
    const categoryGroupTitles = filters.map(
      (filter) => filter.categoryGroupTitle
    );

    // Only get valid category group titles
    return pick(Object.fromEntries(searchParams), categoryGroupTitles);
  };

  const [filterOptions, setFilterOptions] = useState(getQueryObjectFromUrl());

  const [
    fetchedExperiences,
    status,
    haveMoreExperiences,
    setCategoryIds,
    setFetchAll,
  ] = useExperienceFilter(displayAllEntries);

  const showDisplayAllButton = !displayAllEntries && haveMoreExperiences;

  useEffect(() => {
    const query = getQueryObjectFromUrl();
    setFilterOptions(query);
    const categoryIds = Object.values(query);
    if (initialCategory) {
      // An ID to be used to always filter the entries by regardless of what is set through the UI.
      categoryIds.push(initialCategory);
    }
    setCategoryIds(categoryIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const handleSelectChange = (categoryGroupTitle, categoryID) => {
    const query = getQueryObjectFromUrl();
    const newQueryObject = {
      ...query,
      [categoryGroupTitle]: categoryID,
    };
    const searchParamsString = qs.stringify(newQueryObject);
    history.push({
      pathname: location.pathname,
      search: searchParamsString,
    });
  };

  const handleResetFilterButtonClick = () => {
    const query = getQueryObjectFromUrl();
    const queryForAllObject = mapValues(query, () => '0');
    const searchParamsString = qs.stringify(queryForAllObject);
    history.push({
      pathname: location.pathname,
      search: searchParamsString,
    });
  };

  return (
    <div>
      {/* Filters */}
      <div className="row justify-content-center mb-5">
        {filters.map((filter) => (
          <div
            className="col col-md-4 col-lg-3"
            key={filter.categoryGroupTitle}
          >
            <FloatingLabel
              controlId={filter.categoryGroupTitle}
              label={filter.categoryGroupTitle}
            >
              <Form.Select
                aria-label={`Select ${filter.categoryGroupTitle}`}
                value={filterOptions[filter.categoryGroupTitle]}
                onChange={(e) =>
                  handleSelectChange(filter.categoryGroupTitle, e.target.value)
                }
              >
                <option value="0">Viewing All</option>
                {filter.filters.map((category) => (
                  <option value={category.categoryID} key={category.categoryID}>
                    {category.categoryLabel}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
          </div>
        ))}
      </div>

      {/* Cards */}
      <ul className="row gy-5">
        {status === 'succeed' &&
          fetchedExperiences.map((entry) => (
            <li className="col-12 col-sm-6 col-md-4" key={entry.title}>
              {/* eslint-disable-next-line react/jsx-props-no-spreading */}
              <ExperienceCard {...entry} />
            </li>
          ))}

        {status === 'loading' &&
          [...Array(6)].map((e, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className="col-12 col-sm-6 col-md-4" key={index}>
              <CardSkeleton />
            </div>
          ))}
      </ul>

      {/* Empty state */}
      {status === 'succeed' && isEmpty(fetchedExperiences) && (
        <div className="d-flex flex-column align-items-center gap-3 mt-5">
          <p className="fw-bold text-x-large">Sorry, no products found</p>
          <p>
            Try adjusting your {pluralize('filters', filters.length)} to find
            other products
          </p>
          <Button
            variant="outline-primary"
            onClick={handleResetFilterButtonClick}
          >
            Reset {pluralize('filters', filters.length)}
          </Button>
        </div>
      )}

      {/* Button - only shown in experience index page  */}
      {showDisplayAllButton && (
        <div className="d-flex justify-content-center mt-5">
          <Button variant="outline-primary" onClick={() => setFetchAll(true)}>
            View all products
          </Button>
        </div>
      )}
    </div>
  );
};

const ExperienceFilter = (props) => (
  <BrowserRouter>
    {/* eslint-disable-next-line react/jsx-props-no-spreading */}
    <ExperienceFilterKernel {...props} />
  </BrowserRouter>
);

export { ExperienceFilter, ExperienceFilterKernel };

ExperienceFilterKernel.propTypes = {
  initialCategory: PropTypes.number,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      categoryGroupTitle: PropTypes.string,
      filters: PropTypes.arrayOf(
        PropTypes.shape({
          categoryID: PropTypes.number,
          categoryLabel: PropTypes.string,
        })
      ),
    })
  ),
  displayAllEntries: PropTypes.bool,
};
